import { FormEvent, useContext, useEffect } from "react";
import styled, { useTheme } from "styled-components";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { DashValidation } from "dash-common";
import ReactGA from "react-ga4";
import {
  Button,
  Flex,
  Form,
  FormContext,
  googlePlaceToAddress,
  Text,
  useScreenResolution,
  useServerResponseTranslation,
  ViewerContext,
  Card,
  toast,
} from "dash-ui";
import {
  GetCurrentBrokerDocument,
  useUpdateCurrentBrokerMutation,
} from "gql/generated.graphql";

import { RoutesPath } from "flow-components/routes/routes";
import ProfileCard from "pages/authentication/complete-sign-up-scope/AddAdditionalInfoPage/component/AddAdditionalInfoForm/components/ProfileCard/ProfileCard";
import { FormFields } from "pages/authentication/complete-sign-up-scope/AddAdditionalInfoPage/component/AddAdditionalInfoForm/constants/constants";
import useValidation from "pages/authentication/complete-sign-up-scope/AddAdditionalInfoPage/component/AddAdditionalInfoForm/hooks/useValidation";
import usePriceValidation from "pages/authentication/complete-sign-up-scope/AddAdditionalInfoPage/component/AddAdditionalInfoForm/components/SettingsCard/hooks/useValidation";

const ButtonStyle = styled(Button)`
  @media ${({ theme }) => theme.devices.mobile} {
    min-width: unset;
    padding-left: 20px;
    padding-right: 20px;
  }
`;

function AddAdditionalInfoForm() {
  const theme = useTheme();
  const { t } = useTranslation(["pages", "common"]);
  const navigate = useNavigate();
  const {
    devices: { isMobile },
  } = useScreenResolution();
  const { account, profile } = useContext(ViewerContext);
  const { formState, formOnSubmit, setFormValues } = useContext(FormContext);
  const [updateBroker, { loading }] = useUpdateCurrentBrokerMutation({
    refetchQueries: [GetCurrentBrokerDocument],
  });
  const translateErrorMessage = useServerResponseTranslation();
  const valid = useValidation();
  const validPrice = usePriceValidation();

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.stopPropagation();
    formOnSubmit();
    const { agencyName, agencyWebsite, shortDescription } = formState;

    if (
      !valid(FormFields.AgencyName, agencyName) ||
      !valid(FormFields.AgencyWebsite, agencyWebsite) ||
      !valid(FormFields.ShortDescription, shortDescription)
    ) {
      return null;
    }
    const address = formState.place && googlePlaceToAddress(formState.place);
    const input = {
      description: formState.shortDescription || null,
      agencyName: formState.agencyName || null,
      agencyWebsite: formState.agencyWebsite || null,
      preferredPropertySettingsInput: {
        locationInput: formState.place
          ? {
              street: address.street || null,
              zipCode: address.zipCode || null,
              city: address.city || null,
              county: address.county || null,
              state: address.state || null,
              googlePlaceId: formState.place.place_id,
              lat: formState.place.geometry.location.lat(),
              lng: formState.place.geometry.location.lng(),
            }
          : null,
        buildingTypes: formState.buildingTypes || null,
        priceRange: {
          min: formState.minPropertyPrice,
          max: formState.anyPrice ? null : formState.maxPropertyPrice,
        },
        radiusInMiles: formState.radius || null,
      },
      profileInput: {
        avatarFileId: formState.avatar?._id || null,
      },
    };
    updateBroker({
      variables: { input },
    })
      .then(() => {
        ReactGA.event("sign_up");
        navigate(RoutesPath.Properties);
      })
      .catch((error) => toast.error(translateErrorMessage(error)));
  };
  useEffect(() => {
    // FIXME: re-creating context functions everytime state change. Use useCallback
    if (account) {
      setFormValues({
        [FormFields.Avatar]: profile?.avatarFile || null,
        [FormFields.Radius]: DashValidation.DefaultLocationRadius,
        [FormFields.MinPropertyPrice]: DashValidation.MinPropertyPrice,
        [FormFields.MaxPropertyPrice]: DashValidation.MaxPropertyPrice,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account]);

  return (
    <Form id="additional-info-form" className="additional-info-form" w="100%" onSubmit={onSubmit}>
      <Flex flexDirection="column" w="100%">
        <Flex mt={[4, 12, 12, 12]} justifyContent="space-between" w="100%">
          <Flex flexDirection="column">
            <Text bold xxl>
              {t("pages:completeSignUpAddAdditionalInfoPage:header")}
            </Text>
            <Text color={theme.colors.n80}>
              {t("pages:completeSignUpAddAdditionalInfoPage:subheader")}
            </Text>
          </Flex>
          <ButtonStyle
            type="submit"
            disabled={loading}
            isLoading={loading}
            minWidth={124}
            medium={!isMobile}
            small={isMobile}
          >
            {t("common:labels:next")}
          </ButtonStyle>
        </Flex>
        <Card mt={8} p={6} w="100%">
          <ProfileCard />
        </Card>
      </Flex>
    </Form>
  );
}

export default AddAdditionalInfoForm;
